import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Users, Plus, Search, Building2, Phone, Mail, Calendar,
  MoreHorizontal, Edit, Trash2, CheckCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const AffiliateLeadsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Demo leads
  const [leads, setLeads] = useState([
    { id: 1, company: 'TechStart SAS', contact: 'Marie Dupont', email: 'marie@techstart.fr', phone: '06 12 34 56 78', status: 'qualified', notes: 'Intéressé par refonte site', created: '2024-12-20' },
    { id: 2, company: 'Design Studio', contact: 'Pierre Martin', email: 'pierre@designstudio.fr', phone: '06 98 76 54 32', status: 'contacted', notes: 'A rappeler la semaine prochaine', created: '2024-12-18' },
    { id: 3, company: 'WebAgency Pro', contact: 'Sophie Leroy', email: 'sophie@webagency.fr', phone: '06 11 22 33 44', status: 'converted', notes: 'Projet signé - 8000€', created: '2024-12-15' },
  ]);

  const [newLead, setNewLead] = useState({
    company: '', contact: '', email: '', phone: '', notes: ''
  });

  const statusConfig = {
    'new': { color: 'bg-blue-100 text-blue-700', label: 'Nouveau' },
    'contacted': { color: 'bg-amber-100 text-amber-700', label: 'Contacté' },
    'qualified': { color: 'bg-purple-100 text-purple-700', label: 'Qualifié' },
    'converted': { color: 'bg-emerald-100 text-emerald-700', label: 'Converti' },
    'lost': { color: 'bg-slate-100 text-slate-700', label: 'Perdu' },
  };

  const handleAddLead = () => {
    if (!newLead.company || !newLead.contact) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    const lead = {
      id: leads.length + 1,
      ...newLead,
      status: 'new',
      created: new Date().toISOString().split('T')[0]
    };
    setLeads([lead, ...leads]);
    setNewLead({ company: '', contact: '', email: '', phone: '', notes: '' });
    setShowAddModal(false);
    toast.success('Lead ajouté avec succès !');
  };

  const filteredLeads = leads.filter(lead => 
    lead.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.contact.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Mes Leads</h1>
            <p className="text-slate-500">{leads.length} lead(s) au total</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
            <Plus className="w-4 h-4 mr-2" /> Ajouter un lead
          </Button>
        </div>

        <Card className="mb-6 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher un lead..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <Card key={lead.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: theme.colors?.primary }}>{lead.company}</h3>
                      <p className="text-sm text-slate-500">{lead.contact}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={statusConfig[lead.status]?.color}>{statusConfig[lead.status]?.label}</Badge>
                    <span className="text-sm text-slate-400">{lead.created}</span>
                  </div>
                </div>
                {lead.notes && (
                  <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">{lead.notes}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Lead Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ajouter un nouveau lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Entreprise *</Label>
                <Input value={newLead.company} onChange={(e) => setNewLead({...newLead, company: e.target.value})} placeholder="Nom de l'entreprise" />
              </div>
              <div>
                <Label>Contact *</Label>
                <Input value={newLead.contact} onChange={(e) => setNewLead({...newLead, contact: e.target.value})} placeholder="Nom du contact" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input value={newLead.email} onChange={(e) => setNewLead({...newLead, email: e.target.value})} type="email" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={newLead.notes} onChange={(e) => setNewLead({...newLead, notes: e.target.value})} placeholder="Informations complémentaires..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
              <Button onClick={handleAddLead} style={{ backgroundColor: theme.colors?.accent }}>Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AffiliateLeadsPage;